/**
 * ConversationDetailsView
 * Main component for displaying conversation details with real-time updates via Socket.IO
 * Migrated from legacy class component to React functional component with hooks
 */

import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useConversationBus } from '@/hooks/useConversationBus';
import { InfoWindowContext } from '@/contexts/InfoWindowContext';
import { conversationBus } from '@/lib/conversationBus';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { socketService } from '@/services/socketService';
import { apiService } from '@/services/api';
import _ from 'lodash';

import { ConversationItem } from '@/components/ConversationItem';
import ConversationItemAnimation from './ConversationItemAnimation';
import ConversationApi from './api/ConversationDetailsApi';

import type {
  ConversationDetailsViewProps,
  DataConversation,
  FunctionsLayers,
  UpdateTabState,
  SocketShowRefreshButtonData,
  ReactFunctions,
  LayerData,
} from './types';
import type { ConversationDetail, Reply, LayerCustomField } from '@/types/conversation';

import EditCard from '@/components/EditCard/EditCard';
import type { DuplicateCardRef, EditCardRef } from '@/components/EditCard/EditCard';
import './ConversationDetailsView.css';
import DuplicateCard from '../DuplicateCard/DuplicateCard';
import { buildConversationDetailsUrl, normalizeConversationId } from '@lib/conversationUrl';

const ConversationDetailsView: React.FC<ConversationDetailsViewProps> = ({ request }) =>
{
  // Use the global singleton connection ID
  const globalSocketConnectionId = useSocketConnectionId();

  // State
  const [dataConversation, setDataConversation] = useState<Partial<DataConversation>>({});
  const [reloadKey, setReloadKey] = useState(0);
  const activeTabRef = useRef(0);
  const activeShowDetailsRef = useRef(false);
  const [functionsLayers, setFunctionsLayers] = useState<FunctionsLayers>({});
  const [socketConnectionId, setSocketConnectionId] = useState<string>(
    request.isInfoWindow && request.params.SocketConnectionId
      ? request.params.SocketConnectionId
      : socketService.connectionId,
  );
  const [showReloadButton, setShowReloadButton] = useState(false);
  const [updateCustomFields, setUpdateCustomFields] = useState(false);
  const [updateConversation, setUpdateConversation] = useState(false);
  const [forceReloadDetails, setForceReloadDetails] = useState<
    ((params: unknown, forceReload: boolean) => void) | null
      >(null);
  const [forceReloadDetailsParams, setForceReloadDetailsParams] = useState<unknown>({});
  const [updateReply, setUpdateReply] = useState(false);
  const [replyToHighlight, setReplyToHighlight] = useState<number | null>(null);
  const [shouldUpdateHideReplies, setShouldUpdateHideReplies] = useState(false);
  // Start loading on the initial open so the skeleton shows consistently; silent
  // reloads pass disableLoading=true while isLoading is already false, staying silent.
  const [isLoading, setIsLoading] = useState(true);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [_isDarkMode] = useState<boolean>(() =>
  {
    try
    {
      return JSON.parse(sessionStorage.getItem('C2_enableDarkMode') || 'false');
    }
    catch
    {
      return false;
    }
  });
  const [updateTab, setUpdateTab] = useState<UpdateTabState>({
    ConversationPhaseId: 0,
    Enable: false,
  });

  // Refs
  const conversationItemRef = useRef<any>(null);
  const editCardRef = useRef<EditCardRef>(null);
  const editCardInitRef = useRef<((conv: any, itemType?: any) => void) | null>(null);
  const duplicateCardRef = useRef<DuplicateCardRef>(null);
  const duplicateCardInitRef = useRef<((conv: any) => void) | null>(null);
  // Prevents double fetch from React StrictMode (mount → cleanup → mount)
  const hasFetchedConversationRef = useRef<number | null>(null);
  // Mirrors `isLoading` for use inside getConversation. getConversation's useCallback
  // deps don't include isLoading (see comment there), so in contexts where `request`
  // is a stable object reference across renders (e.g. InfoWindow, which memoizes the
  // element it passes down) the callback would otherwise never pick up a fresh
  // isLoading value and would permanently see its initial `true`. That broke the
  // "silent reload" branch for disableLoading=true calls (e.g. reply-added), forcing
  // a visible isLoading flip -> skeleton flash -> flip back, even though nothing
  // about the reload was supposed to be visible.
  const isLoadingRef = useRef(isLoading);
  useEffect(() =>
  {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Handle socket refresh button events
  const handleSocketRefreshButton = useCallback(
    (data: SocketShowRefreshButtonData) =>
    {
      try
      {
        const currentConversation = dataConversation.conversation;
        if (!currentConversation) return;

        if (socketConnectionId !== data.SocketConnectionId)
        {
          if (currentConversation.ConversationId === data.relatedConversationId)
          {
            switch (data.type)
            {
              case 'NEWDEFITEM':
                setTimeout(() =>
                {
                  setShowReloadButton(true);
                  setUpdateConversation(true);
                }, 0);
                return;
            }
          }
        }

        if (
          currentConversation.ConversationId === data.conversationId ||
          (data.IsProject && currentConversation.ProjectId === data.ProjectId)
        )
        {
          if (!request.params?.ShowRefreshButton) return;
        }
        else if (data.type !== 'MOVECONVTOCOLUMN')
        {
          return;
        }

        const valueSocketConnectionId = data.SocketConnectionId;
        if (
          valueSocketConnectionId &&
          socketConnectionId !== valueSocketConnectionId &&
          valueSocketConnectionId !== 'null'
        )
        {
          switch (data.type)
          {
            case 'UPDATEDASHBOARDITEM': {
              let reloadConversation = true;
              if (
                data.notificationTypeCode === 'BOOKMARK' &&
                data.personId?.toString() !== request.api.PersonId
              )
              {
                reloadConversation = false;
              }
              setShowReloadButton(reloadConversation);
              setReplyToHighlight(data.ReplyId || null);

              if (reloadConversation)
              {
                if (data.isFullUpdate)
                {
                  setUpdateCustomFields(true);
                  setUpdateConversation(true);
                }
                else if (data.isReplyUpdate)
                {
                  setUpdateReply(true);
                }
                else if (data.isCustomFieldUpdated)
                {
                  setUpdateCustomFields(true);
                  setUpdateConversation(true);
                }
                else
                {
                  setUpdateConversation(true);
                }
              }
              break;
            }
            case 'NEWDEFITEM':
              setShowReloadButton(true);
              break;
            case 'MOVECONVTOCOLUMN': {
              const reloadConversation = true;
              if (
                (data.notificationTypeCode === 'BOOKMARK' &&
                  data.personId?.toString() !== request.api.PersonId) ||
                (request.isConversationDetailsView && data.notificationTypeCode !== 'BOOKMARK')
              )
              {
                return;
              }
              setShowReloadButton(reloadConversation);
              break;
            }
          }
        }
      }
      catch (e)
      {
        console.error('Error handling socket refresh button:', e);
      }
    },
    [dataConversation.conversation, socketConnectionId, request],
  );

  // Get conversation by ID
  const getConversation = useCallback(
    async (
      conversationId: number,
      _layersToHide = '',
      getAllReplies = false,
      _isConversationDetailsView = false,
      totalReplies = 0,
      reload = false,
      cb: ((response: ConversationDetail) => void) | null = null,
      disableLoading = false,
      phaseId: number | null = null,
      // When true, the currently rendered Replies window is kept as-is instead of being
      // replaced by the response's Replies. Used by silent background refreshes (custom
      // fields sync after reply-added/updated) that call with getAllReplies=false, whose
      // truncated Replies would otherwise collapse an already-expanded "See more" list
      // back down to the default collapsed window.
      preserveReplies = false,
    ) =>
    {
      if (!isLoadingRef.current && disableLoading)
      {
        setIsLoading(false);
        setShowErrorMessage(false);
      }
      else
      {
        setIsLoading(true);
        setShowErrorMessage(false);
      }

      const isenabled = request.RecycleBin !== true;

      const requestParams = {
        conversationId,
        layersToHide: '',
        getAllReplies,
        isConversationDetailsView: true,
        totalReplies,
        isEnabled: isenabled,
        phaseId,
      };

      setTimeout(async () =>
      {
        try
        {
          const response = await ConversationApi.getConversationById(request.api, requestParams);

          if (response)
          {
            // Fix image URLs
            for (let x = 0; x < response.Replies.length; x++)
            {
              if (response.Replies[x]!.ImageName === 'Images/user.png')
              {
                const baseUrl = request.config.weburl.replace(/\/$/, '');
                response.Replies[x]!.ImageName = baseUrl + '/Images/user.png';
              }
            }

            if (request.params.DashboardGroupId)
            {
              response.DashboardGroupId = request.params.DashboardGroupId;
            }

            const conversation: DataConversation = {
              conversation: response,
              realItemTypes: request.realItemTypes,
              api: request.api,
              corgeeFunctions: request.functions,
              isConversationDetailsView: request.isConversationDetailsView ?? true,
              onUpdateConversation: handleUpdateConversation,
              onAddReply: handleAddReply,
              onDeleteReply: handleDeleteReply,
              onUpdateReply: handleUpdateReply,
              onCreateNewItem: handleCreateNewItem,
              onDuplicateItem: handleDuplicateItem,
              onConvertTo: handleConvertTo,
              onAddLayerToCard: handleAddLayerToCard,
              onSetFunctionsCardLayers: setFunctionsLayers,
              SocketConnectionId:
                socketConnectionId !== ''
                  ? socketConnectionId
                  : request.params.SocketConnectionId || '',
              configMenus: request.params?.ConfigMenus,
              isInfoWindow: request.isInfoWindow ?? false,
              getForceReloadDetailsFn: handleGetForceReloadDetailsFn,
              selectedPhaseId: phaseId,
            };

            if (preserveReplies)
            {
              setDataConversation(prev =>
              {
                // Keep whatever Replies window is currently rendered (including any
                // optimistic adds/edits already applied) — this response's Replies may
                // be a truncated page (getAllReplies=false) that would otherwise
                // collapse an already-expanded "See more" list back to the default.
                const currentReplies =
                  prev.conversation?.ConversationId === response.ConversationId
                    ? prev.conversation.Replies
                    : response.Replies;
                return {
                  ...conversation,
                  conversation: { ...response, Replies: currentReplies },
                };
              });
            }
            else
            {
              setDataConversation(conversation);
            }
            if (disableLoading) setReloadKey(k => k + 1);
            setIsLoading(false);

            // Handle info window chat cache
            if (request.isInfoWindowChat)
            {
              handleInfoWindowChatCache(conversationId, response);
            }

            if (reload && forceReloadDetails)
            {
              forceReloadDetails(forceReloadDetailsParams, true);
            }
            else
            {
              sendDatatoCallback(response);
            }

            if (cb && typeof cb === 'function')
            {
              cb(response);
            }

            if (replyToHighlight !== null)
            {
              setShouldUpdateHideReplies(true);
            }
          }
          else
          {
            setShowErrorMessage(true);
            setIsLoading(false);
            sendError(conversationId);
          }
        }
        catch (error)
        {
          console.error('Error fetching conversation:', error);
          setShowErrorMessage(true);
          setIsLoading(false);
          sendError(conversationId);
        }
      }, disableLoading ? 0 : 1000);
    },
    [request, socketConnectionId, forceReloadDetails, forceReloadDetailsParams, replyToHighlight],
  );

  // Handle info window chat cache
  const handleInfoWindowChatCache = async (
    conversationId: number,
    conversation: ConversationDetail,
  ) =>
  {
    let conversationsCacheChat: Array<{
      ConversationId: number;
      Title: string;
      ProjectName?: string;
    }> = [];

    const storageValue = localStorage.getItem('C2_objStorageConversation');
    if (!storageValue || storageValue === '')
    {
      localStorage.setItem('C2_objStorageConversation', '[]');
      conversationsCacheChat = [];
    }
    else
    {
      try
      {
        conversationsCacheChat = JSON.parse(storageValue);
      }
      catch
      {
        conversationsCacheChat = [];
      }
    }

    if (!conversationsCacheChat.find(item => item.ConversationId === conversationId))
    {
      if (conversationsCacheChat.length >= 100)
      {
        conversationsCacheChat.shift();
      }
      conversationsCacheChat.push({
        ConversationId: conversation.ConversationId,
        Title: conversation.Title,
        ProjectName: conversation.ProjectName,
      });
    }

    // Filter conversations without ProjectName and fetch their data
    const conversationsWithoutPN = conversationsCacheChat.filter(item => !('ProjectName' in item));

    if (conversationsWithoutPN.length > 0)
    {
      try
      {
        const response = await ConversationApi.GetConversationsDataByConversationIds(request.api, {
          conversationsIds: conversationsWithoutPN.map(x => x.ConversationId).join(', '),
        });

        const conversations = response.map((obj: any) => ({
          ConversationId: obj.ConversationId,
          Title: obj.Title,
          ProjectName: obj.ProjectName,
        }));

        const conversationsWithPN = conversationsCacheChat.filter(item => 'ProjectName' in item);
        const totalArray = conversationsWithPN.concat(conversations);
        localStorage.setItem('C2_objStorageConversation', JSON.stringify(totalArray));
      }
      catch (error)
      {
        console.error('Error fetching conversations data:', error);
      }
    }
    else
    {
      localStorage.setItem('C2_objStorageConversation', JSON.stringify(conversationsCacheChat));
    }
  };

  // Send data to callback
  const sendDatatoCallback = (conversation: ConversationDetail) =>
  {
    if (request.functions?.onLoadDataCallback)
    {
      request.functions.onLoadDataCallback(conversation);
    }
  };

  // Send error
  const sendError = (conversationId: number) =>
  {
    if (request.functions?.onSendError)
    {
      request.functions.onSendError(conversationId);
    }
  };

  // Handle force reload details function
  const handleGetForceReloadDetailsFn = useCallback(
    (fn: (params: unknown, forceReload: boolean) => void, config: unknown) =>
    {
      setForceReloadDetails(() => fn);
      setForceReloadDetailsParams(config);
    },
    [],
  );

  // Handle update conversation
  const handleUpdateConversation = useCallback(
    (id: number, newAttrs: Partial<ConversationDetail>, sendUpdate = true, renderType?: string) =>
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation) return prev;

        const conversationCopy = { ...prev, conversation: { ...prev.conversation, reactSelfUpdate: true } };
        Object.entries(newAttrs).forEach(([key, value]) =>
        {
          (conversationCopy.conversation as any)[key] = value;
        });

        if (sendUpdate && request.functions?.onUpdateConversation)
        {
          request.functions.onUpdateConversation(
            id,
            newAttrs,
            conversationCopy.conversation,
            renderType,
          );
        }

        return conversationCopy;
      });

      if (newAttrs.Title)
      {
        conversationBus.emit('title-updated', {
          conversationId: id,
          title: newAttrs.Title,
        });
      }
    },
    [request.functions],
  );

  // Handle update conversation external
  const handleUpdateConversationExternal = useCallback(
    (
      id: number,
      newAttrs: Partial<ConversationDetail>,
      replace?: boolean,
      highlight?: boolean,
      replaceReplies?: boolean,
    ) =>
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation || prev.conversation.ConversationId !== parseInt(String(id)))
          return prev;

        const conversationCopy = { ...prev };

        if (highlight)
        {
          (newAttrs as any).Highlight = true;
        }

        if (replace)
        {
          const bkpReplies = [...prev.conversation.Replies];
          conversationCopy.conversation = newAttrs as ConversationDetail;
          if (replaceReplies)
          {
            conversationCopy.conversation.Replies = bkpReplies;
          }
        }
        else
        {
          conversationCopy.conversation = { ...prev.conversation };
          Object.entries(newAttrs).forEach(([key, value]) =>
          {
            (conversationCopy.conversation as any)[key] = value;
          });
        }

        return conversationCopy;
      });
    },
    [],
  );

  const handleUpdateConversationPhoto = useCallback((id: number, updates: Record<string, any>) =>
  {
    setDataConversation(prev =>
    {
      if (!prev.conversation || prev.conversation.ConversationId !== parseInt(String(id)))
      {
        return prev;
      }

      const conversationCopy = { ...prev };
      conversationCopy.conversation = { ...prev.conversation };

      const layersKey = (conversationCopy.conversation as any).LayerCustomFields
        ? 'LayerCustomFields'
        : 'layerCustomFields';
      const currentLayers = (conversationCopy.conversation as any)[layersKey] || [];

      if (updates.Title)
      {
        conversationCopy.conversation!.Title = updates.Title;
      }

      if (updates.IsRedFlag !== undefined)
      {
        (conversationCopy.conversation as any).IsRedFlag = updates.IsRedFlag;
      }

      if (updates.IsImportant !== undefined)
      {
        (conversationCopy.conversation as any).IsImportant = updates.IsImportant;
      }

      if (updates.ConversationLayers !== undefined)
      {
        (conversationCopy.conversation as any).ConversationLayers = updates.ConversationLayers;
      }

      if (currentLayers && currentLayers.length > 0 && updates.imageUrl)
      {
        const layers = [...currentLayers];
        const layerZeroIdx = layers.findIndex((l: any) => l.LayerId === 0);
        if (layerZeroIdx >= 0)
        {
          const layer = { ...layers[layerZeroIdx] };
          if (layer.CustomFields)
          {
            layer.CustomFields = layer.CustomFields.map((f: any) =>
              f.Description === 'Photo' ? { ...f, Value: updates.imageUrl } : f,
            );
            layers[layerZeroIdx] = layer;
            (conversationCopy.conversation as any)[layersKey] = layers;
          }
        }
      }

      return conversationCopy;
    });
  }, []);

  // Handle external reply update
  const handleUpdateExternalReply = useCallback(
    (_convId: number, replyId: number, newAttrs: Partial<Reply>, replace = false) =>
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation) return prev;

        const conversationCopy = _.cloneDeep(prev);
        const indexReply = conversationCopy.conversation!.Replies.findIndex(
          r => r.ReplyId === replyId,
        );

        if (indexReply >= 0 && conversationCopy.conversation!.Replies[indexReply])
        {
          if (replace)
          {
            conversationCopy.conversation!.Replies[indexReply] = newAttrs as Reply;
          }
          else
          {
            Object.entries(newAttrs).forEach(([key, value]) =>
            {
              (conversationCopy.conversation!.Replies[indexReply] as any)[key] = value;
            });
          }
        }

        return conversationCopy;
      });
    },
    [],
  );

  // Handle add reply
  const handleAddReply = useCallback(
    (convId: number, reply: Reply, sendUpdate = true, updateTotalReplies = true) =>
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation || prev.conversation.ConversationId !== convId) return prev;

        const conversationCopy = _.cloneDeep(prev);

        // Check phases
        if (conversationCopy.conversation!.HasPhases && reply.ConversationPhaseId)
        {
          // TODO: Implement phase checking logic
        }

        // Fix image URL
        if (reply.ImageName)
        {
          const imgLower = reply.ImageName.toLowerCase();
          if (imgLower.includes('images/user.png'))
          {
            const baseUrl = request.config.weburl.replace(/\/$/, '');
            reply.ImageName = baseUrl + '/Images/user.png';
          }
        }

        // Filter and add reply
        conversationCopy.conversation!.Replies = conversationCopy.conversation!.Replies.filter(
          x => x.ReplyId > 0 && x.ReplyId !== reply.ReplyId,
        );
        conversationCopy.conversation!.Replies.push(reply);

        if (updateTotalReplies)
        {
          if (reply.OriginalConversationId > 0)
          {
            conversationCopy.conversation!.TotalTasks += 1;
          }
          else
          {
            conversationCopy.conversation!.TotalReplies += 1;
          }
        }

        if (sendUpdate && request.functions?.onAddReply)
        {
          request.functions.onAddReply(convId, reply, {
            ...conversationCopy.conversation!,
          } as ConversationDetail);
        }

        return conversationCopy;
      });
    },
    [request.config.weburl, request.functions],
  );

  // Handle update reply
  const handleUpdateReply = useCallback(
    (
      convId: number,
      replyId: number,
      newAttrs: Partial<Reply>,
      newTotalFavorites?: number,
      sendUpdate = true,
    ) =>
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation || prev.conversation.ConversationId !== convId) return prev;

        const conversationCopy = _.cloneDeep(prev);
        const indexReply = conversationCopy.conversation!.Replies.findIndex(
          r => r.ReplyId === replyId,
        );

        let replyUpdated: Reply | undefined;
        if (indexReply >= 0 && conversationCopy.conversation!.Replies[indexReply])
        {
          Object.entries(newAttrs).forEach(([key, value]) =>
          {
            (conversationCopy.conversation!.Replies[indexReply] as any)[key] = value;
          });
          replyUpdated = conversationCopy.conversation!.Replies[indexReply];
        }

        if (sendUpdate && request.functions?.onUpdateReply)
        {
          request.functions.onUpdateReply(
            conversationCopy.conversation!,
            replyUpdated,
            newAttrs,
            newTotalFavorites,
          );
        }

        return conversationCopy;
      });
    },
    [request.functions],
  );

  // Handle delete reply
  const handleDeleteReply = useCallback(
    (convId: number, replyId: number, callback?: (totalReplies?: number) => void) =>
    {
      if (isNaN(convId)) return;

      setDataConversation(prev =>
      {
        if (!prev.conversation || prev.conversation.ConversationId !== convId || isNaN(replyId))
          return prev;

        const conversationCopy = _.cloneDeep(prev);
        const indexReplyToDelete = conversationCopy.conversation!.Replies.findIndex(
          r => r.ReplyId === parseInt(String(replyId)),
        );

        let totalReplies: number | undefined;

        if (indexReplyToDelete >= 0)
        {
          // Decrease TotalFavorites counter if reply was marked as favorite
          if (conversationCopy.conversation!.Replies[indexReplyToDelete]!.IsFavorite)
          {
            conversationCopy.conversation!.TotalFavorites--;
          }

          conversationCopy.conversation!.Replies.splice(indexReplyToDelete, 1);
          conversationCopy.conversation!.TotalReplies -= 1;
          totalReplies = conversationCopy.conversation!.TotalReplies;

          // Delete warning reply
          const warningReplyIndex = conversationCopy.conversation!.Replies.findIndex(reply =>
            reply.Body.includes(`SubmitReplyWarningId="${replyId}"`),
          );
          if (warningReplyIndex >= 0)
          {
            conversationCopy.conversation!.Replies.splice(warningReplyIndex, 1);
            conversationCopy.conversation!.TotalReplies -= 1;
            totalReplies = conversationCopy.conversation!.TotalReplies;
          }
        }

        if (typeof callback === 'function')
        {
          setTimeout(() => callback(totalReplies), 0);
        }

        return conversationCopy;
      });
    },
    [],
  );

  // Handle delete conversation
  const handleDeleteConversation = useCallback((convId: number, _convObj?: unknown) =>
  {
    setDataConversation(prev =>
    {
      if (!prev.conversation) return prev;

      const conversationCopy = _.cloneDeep(prev);

      // Search subitems
      const replySubItem = conversationCopy.conversation!.Replies.find(
        r => r.OriginalConversationId === convId,
      );

      if (replySubItem)
      {
        const replyIndex = conversationCopy.conversation!.Replies.findIndex(
          r => r.ReplyId === replySubItem.ReplyId,
        );

        if (replyIndex >= 0)
        {
          conversationCopy.conversation!.Replies.splice(replyIndex, 1);
          conversationCopy.conversation!.TotalTasks = Math.max(
            0,
            conversationCopy.conversation!.TotalTasks - 1,
          );
        }
      }

      if (conversationCopy.conversation!.ConversationId === convId)
      {
        (conversationCopy.conversation as any).IsDeleted2 = true;
      }

      return conversationCopy;
    });
  }, []);

  // Child tasks deleted elsewhere (Small Card, another Info Window) must drop their reply here too
  const dataConversationRef = useRef(dataConversation);
  dataConversationRef.current = dataConversation;
  useEffect(() =>
  {
    return conversationBus.on('conversation-deleted', ({ conversationId }) =>
    {
      const hasTaskReply = dataConversationRef.current.conversation?.Replies?.some(
        r => r.OriginalConversationId === conversationId,
      );
      if (hasTaskReply) handleDeleteConversation(conversationId);
    });
  }, [handleDeleteConversation]);

  // Handle create new item
  const handleCreateNewItem = useCallback(
    (item: any) =>
    {
      if (item.SocketConnectionId != null)
      {
        setDataConversation(prev =>
        {
          if (!prev.conversation) return prev;
          return {
            ...prev,
            conversation: { ...prev.conversation, SocketConnectionId: item.SocketConnectionId },
          };
        });
      }

      if (request.functions?.onCreateItemFromCustomType && dataConversation.conversation)
      {
        request.functions.onCreateItemFromCustomType(item, dataConversation.conversation);
      }
    },
    [request.functions, dataConversation.conversation],
  );

  // Handle duplicate item
  const handleDuplicateItem = useCallback(
    (item: any) =>
    {
      if (item.SocketConnectionId != null)
      {
        setDataConversation(prev =>
        {
          if (!prev.conversation) return prev;
          return {
            ...prev,
            conversation: { ...prev.conversation, SocketConnectionId: item.SocketConnectionId },
          };
        });
      }

      if (request.functions?.onDuplicateItem && dataConversation.conversation)
      {
        request.functions.onDuplicateItem(item, dataConversation.conversation);
      }
    },
    [request.functions, dataConversation.conversation],
  );

  // Handle convert to
  const handleConvertTo = useCallback(
    (conversation: ConversationDetail) =>
    {
      if (request.functions?.onItemConvertTo)
      {
        request.functions.onItemConvertTo(conversation);
      }
    },
    [request.functions],
  );

  // Handle add layer to card — matches legacy editConversationReplyLayerFromItem:
  // 1. Add the layer to the parent conversation via UpdateLayers API.
  // 2. Update local conversation state so CCL re-renders with the new layer enabled.
  // 3. Notify CCL (onUpdateLayerToCard) to add the layer to the reply's selection.
  // 4. Also call the Angular bridge (if present) for socket/UI propagation on the Angular side,
  //    but with a no-op callback to avoid double-adding the layer to the reply.
  const handleAddLayerToCard = useCallback(
    async (
      e: React.MouseEvent | MouseEvent,
      layer: LayerData,
      functions?: { onUpdateLayerToCard?: (layerId: number) => void },
    ) =>
    {
      if (!dataConversation.conversation) return;

      const conv = dataConversation.conversation;

      // ConfigValueId 9 is excluded per legacy rule
      if ((conv as any).ConfigValueId === 9) return;

      const alreadyPresent = (conv.ConversationLayers || []).some(
        (cl: any) => cl.LayerId === layer.LayerId,
      );

      if (!alreadyPresent)
      {
        const newLayerName = (layer as any).Name || layer.LayerName || '';
        const newLayerColor = (layer as any).Color || layer.LayerColor || '';

        const updatedLayers = [
          ...(conv.ConversationLayers || []),
          {
            ConversationId: conv.ConversationId,
            LayerId: layer.LayerId,
            LayerName: newLayerName,
            Name: newLayerName,
            LayerColor: newLayerColor,
            Color: newLayerColor,
            isForAdd: true,
          },
        ];

        try
        {
          const response = await apiService.put<any[]>(
            'conversation/UpdateLayers',
            { ...conv, ConversationLayers: updatedLayers, SocketConnectionId: socketConnectionId },
          );

          const rawLayers =
            Array.isArray(response) && response.length > 0 ? response : updatedLayers;

          // Always enrich name and color from existing layers or the newly added layer.
          // The API response does not return LayerColor, so we must supply it from
          // the conversation's existing layer data or from the layer being added.
          const existingLayerMap = new Map(
            (conv.ConversationLayers || []).map((l: any) => [l.LayerId, l]),
          );
          const finalLayers = rawLayers.map((l: any) =>
          {
            const src = existingLayerMap.get(l.LayerId);
            const isNewLayer = l.LayerId === layer.LayerId;
            const name =
              l.Name || l.LayerName ||
              src?.Name || src?.LayerName ||
              (isNewLayer ? newLayerName : '');
            const color =
              l.LayerColor || l.Color ||
              src?.LayerColor || src?.Color ||
              (isNewLayer ? newLayerColor : '');
            return {
              ...l,
              Name: name,
              LayerName: name,
              LayerColor: color,
              Color: color,
            };
          });

          setDataConversation(prev =>
          {
            if (!prev.conversation) return prev;
            return {
              ...prev,
              conversation: {
                ...prev.conversation,
                ConversationLayers: finalLayers,
                isAddLayerToCard: true,
              } as any,
            };
          });

          conversationBus.emit('layers-updated', {
            conversationId: conv.ConversationId,
            layers: finalLayers,
          });

          functions?.onUpdateLayerToCard?.(layer.LayerId);
        }
        catch (err)
        {
          console.error('Failed to add layer to card:', err);
        }
      }
      else
      {
        // Layer already on conversation — just update reply selection
        functions?.onUpdateLayerToCard?.(layer.LayerId);
      }

      // Notify Angular bridge for socket propagation and other Angular UI updates.
      // Pass a no-op callback — UpdateLayers was already called above.
      if (request.functions?.addLayerToCard)
      {
        const convCopy = { ...conv };
        if ((convCopy as any).ConfigValueId === undefined)
        {
          (convCopy as any).ConfigValueId = (convCopy as any).selectedConfigValueId;
        }
        request.functions.addLayerToCard(e, convCopy, layer, () =>
        {
          // Intentionally empty: UpdateLayers already handled above.
        });
      }
    },
    [request.functions, dataConversation.conversation],
  );

  // Handle add layer to project (adds layer to both project conversation and current conversation)
  const handleAddLayerToProject = useCallback(
    async (
      _e: React.MouseEvent | MouseEvent,
      layer: LayerData,
      functions?: { onUpdateLayerToCard?: (layerId: number) => void; skipCardUpdate?: boolean; skipProjectSync?: boolean },
    ) =>
    {
      if (!dataConversation.conversation) return;
      const conv = dataConversation.conversation;
      const layerName = (layer as any).Name || layer.LayerName || '';
      const layerColor = (layer as any).Color || layer.LayerColor || '';
      const projectConversationId = (layer as any).ProjectConversationId;

      // Step 1: Add layer to project conversation (independent — never blocks step 2).
      // Mirrors legacy: fetch project conv → fetch its groups → clear CustomFields → POST updateconversation.
      // Skipped when called from a reply context (skipProjectSync = true) — the layer is being
      // assigned to a reply, not added to the card, so there is no reason to touch the project conversation.
      // Skip when ConfigValueId === 15 (legacy behaviour).
      if (projectConversationId && !functions?.skipProjectSync)
      {
        try
        {
          const projectConv = await apiService.post<any>(
            `conversation/GetConversationById?conversationId=${projectConversationId}`,
            null,
          );
          if (projectConv && projectConv.ConfigValueId !== 15)
          {
            let groups: any[] = [];
            try
            {
              const fetchedGroups = await apiService.get<any[]>(
                `group/GetGroupsByProject?projectId=${projectConv.ProjectId}`,
              );
              if (Array.isArray(fetchedGroups)) groups = fetchedGroups;
            }
            catch (_) { /* best-effort */ }

            const projectConvToUpdate = {
              ...projectConv,
              CustomFields: [],
              ConversationGroups: groups.length > 0 ? groups : projectConv.ConversationGroups,
              ConversationLayers: [
                ...(projectConv.ConversationLayers || []),
                {
                  ConversationId: projectConv.ConversationId,
                  LayerId: layer.LayerId,
                  LayerName: layerName,
                  LayerColor: layerColor,
                  isForAdd: true,
                  IsActive: 1,
                },
              ],
              SocketConnectionId: socketService.connectionId,
            };
            await apiService.post<any>('conversation/updateconversation', projectConvToUpdate);
          }
        }
        catch (err)
        {
          console.error('Failed to add layer to project conversation:', err);
        }
      }

      // Step 2: Add layer to current conversation — skipped when called from EditCard
      // (skipCardUpdate = true) since EditCard handles its own save flow.
      if (functions?.skipCardUpdate)
      {
        functions.onUpdateLayerToCard?.(layer.LayerId);
        return;
      }

      try
      {
        const alreadyPresent = (conv.ConversationLayers || []).some(
          (cl: any) => cl.LayerId === layer.LayerId,
        );

        if (!alreadyPresent)
        {
          const updatedLayers = [
            ...(conv.ConversationLayers || []),
            {
              ConversationId: conv.ConversationId,
              LayerId: layer.LayerId,
              LayerName: layerName,
              Name: layerName,
              LayerColor: layerColor,
              Color: layerColor,
              isForAdd: true,
            },
          ];

          const response = await apiService.put<any[]>(
            'conversation/UpdateLayers',
            { ...conv, ConversationLayers: updatedLayers, SocketConnectionId: socketConnectionId },
          );

          const rawLayers = Array.isArray(response) && response.length > 0 ? response : updatedLayers;

          const existingLayerMap = new Map(
            (conv.ConversationLayers || []).map((l: any) => [l.LayerId, l]),
          );
          const finalLayers = rawLayers.map((l: any) =>
          {
            if (l.Name || l.LayerName) return l;
            const src = existingLayerMap.get(l.LayerId);
            if (src)
            {
              return {
                ...l,
                Name: src.Name || src.LayerName || '',
                LayerName: src.LayerName || src.Name || '',
                LayerColor: l.LayerColor || src.LayerColor || src.Color || '',
                Color: l.Color || src.Color || src.LayerColor || '',
              };
            }
            if (l.LayerId === layer.LayerId)
            {
              return { ...l, Name: layerName, LayerName: layerName, LayerColor: layerColor, Color: layerColor };
            }
            return l;
          });

          setDataConversation(prev =>
          {
            if (!prev.conversation) return prev;
            return {
              ...prev,
              conversation: {
                ...prev.conversation,
                ConversationLayers: finalLayers,
                isAddLayerToCard: true,
              } as any,
            };
          });

          functions?.onUpdateLayerToCard?.(layer.LayerId);
        }
        else
        {
          functions?.onUpdateLayerToCard?.(layer.LayerId);
        }
      }
      catch (err)
      {
        console.error('Failed to add layer to current conversation:', err);
      }
    },
    [dataConversation.conversation],
  );

  // Handle add layer to card back
  const handleAddLayerToCardBack = useCallback(
    (layerId: number) =>
    {
      if (functionsLayers.updateLayerToCard)
      {
        functionsLayers.updateLayerToCard(layerId);
      }
    },
    [functionsLayers],
  );

  // Handle reload conversation info
  const handleReloadConversationInfo = useCallback(
    (shouldUpdateCustomFields?: boolean) =>
    {
      if (updateCustomFields || shouldUpdateCustomFields)
      {
        // TODO: Call customfield component to reload
      }

      if (updateConversation || updateReply)
      {
        const conversation = dataConversation.conversation;
        if (conversation)
        {
          const repliesToLoad = Math.max(conversation.Replies.length, 10); // Default numReplies
          getConversation(conversation.ConversationId, '', true, false, repliesToLoad);
        }
      }

      setShowReloadButton(false);
      setUpdateCustomFields(false);
      setUpdateConversation(false);
      setUpdateReply(false);
    },
    [
      updateCustomFields,
      updateConversation,
      updateReply,
      dataConversation.conversation,
      getConversation,
    ],
  );

  // Handle reload conversation
  const handleReloadConversation = useCallback(
    async (conversationId: number) =>
    {
      if (dataConversation.conversation?.ConversationId !== conversationId) return;

      const isenabled = request.RecycleBin !== true;

      const requestParams = {
        conversationId,
        layersToHide: '',
        getAllReplies: true,
        isEnabled: isenabled,
        totalReplies: 0,
      };

      try
      {
        const response = await ConversationApi.getConversationById(request.api, requestParams);

        if (response)
        {
          for (let x = 0; x < response.Replies.length; x++)
          {
            if (response.Replies[x]!.ImageName === 'Images/user.png')
            {
              const baseUrl = request.config.weburl.replace(/\/$/, '');
              response.Replies[x]!.ImageName = baseUrl + '/Images/user.png';
            }
          }

          setDataConversation(prev => ({
            ...prev,
            conversation: response,
          }));
          sendDatatoCallback(response);
        }
        else
        {
          sendError(conversationId);
        }
      }
      catch (error)
      {
        sendError(conversationId);
      }

      const conversation = dataConversation.conversation;
      if (conversation && (conversation as any).IsDeleted2)
      {
        if (request.functions?.goToDashboard)
        {
          request.functions.goToDashboard();
        }
      }
    },
    [dataConversation.conversation, request, sendDatatoCallback, sendError],
  );

  // Handle restore conversation
  const handleRestoreConversation = useCallback((convId: number) =>
  {
    setDataConversation(prev =>
    {
      if (!prev.conversation || prev.conversation.ConversationId !== convId) return prev;
      return {
        ...prev,
        conversation: { ...prev.conversation, IsDeleted2: false } as any,
      };
    });
  }, []);

  // Handle update socket connection ID
  const handleUpdateSocketConnectionId = useCallback((newSocketId: string, _isReload?: boolean) =>
  {
    setSocketConnectionId(newSocketId);
    setDataConversation(prev =>
    {
      if (!prev.conversation) return prev;
      return {
        ...prev,
        conversation: { ...prev.conversation, SocketConnectionId: newSocketId },
      };
    });
  }, []);

  // Handle update custom details external
  const handleUpdateCustomDetailsExternal = useCallback(
    (_id: number, details: LayerCustomField[]) =>
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation) return prev;
        return {
          ...prev,
          conversation: { ...prev.conversation, layerCustomFields: details } as any,
        };
      });
    },
    [],
  );

  // Handle update tab
  const handleUpdateTab = useCallback((conversationPhaseId: number) =>
  {
    setUpdateTab({ ConversationPhaseId: conversationPhaseId, Enable: true });
  }, []);

  // Handle stop update tab
  const handleStopUpdateTab = useCallback(() =>
  {
    setUpdateTab({ ConversationPhaseId: 0, Enable: false });
  }, []);

  // Handle changed layers
  const handleChangedLayers = useCallback(
    (params: any, isFromInfoWindow?: boolean) =>
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation) return prev;
        return {
          ...prev,
          conversation: { ...prev.conversation, ConversationLayers: params.ConversationLayers },
        };
      });

      // Only emit here for the updateLayerToCard path (isFromInfoWindow = true).
      // onSelectLayer already emits layers-updated directly — emitting again would
      // double-trigger expensive grid re-renders per selection click.
      if (isFromInfoWindow && params.ConversationId && params.ConversationLayers)
      {
        conversationBus.emit('layers-updated', {
          conversationId: params.ConversationId,
          layers: params.ConversationLayers,
        });
      }

      if (request.functions?.onChangeLayers && dataConversation.conversation)
      {
        request.functions.onChangeLayers(params, isFromInfoWindow);
      }
    },
    [request.functions, dataConversation.conversation],
  );

  // Handle update layers
  const handleUpdateLayers = useCallback(
    (params: any, isFromInfoWindow?: boolean) =>
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation) return prev;
        const updated = {
          ...prev,
          conversation: { ...prev.conversation, ConversationLayers: params },
        };
        if (isFromInfoWindow)
        {
          (updated.conversation as any).isAddLayerToCard = true;
        }
        return updated;
      });

      if (dataConversation.conversation?.ConversationId && params)
      {
        conversationBus.emit('layers-updated', {
          conversationId: dataConversation.conversation.ConversationId,
          layers: params,
        });
      }

      if (request.functions?.onChangeLayers && dataConversation.conversation)
      {
        request.functions.onChangeLayers(
          { ...dataConversation.conversation, ConversationLayers: params },
          isFromInfoWindow,
        );
      }
    },
    [request.functions, dataConversation.conversation],
  );

  // Handle change false layer card
  const handleChangeFalseLayerCard = useCallback(() =>
  {
    if (request.isInfoWindow)
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation) return prev;
        return {
          ...prev,
          conversation: { ...prev.conversation, isAddLayerToCard: false } as any,
        };
      });
    }
  }, [request.isInfoWindow]);

  // Handle submit time
  const handleSubmitTime = useCallback(
    async (conversation: ConversationDetail) =>
    {
      try
      {
        const coretaskid = conversation.CoreTaskId;

        const requestParams = {
          coreTaskId: coretaskid || 0,
          isDeleted: false,
          projectId: conversation.ProjectId,
        };

        const response = await ConversationApi.getCustomFieldsByItemId(request.api, requestParams);

        (conversation as any).CustomFieldLayers = response;
        (conversation as any).layerCustomFields = response;

        handleUpdateCustomDetailsExternal(
          conversation.ConversationId,
          response as LayerCustomField[],
        );

        if (request.functions?.onSubmitTime)
        {
          request.functions.onSubmitTime(conversation);
        }
      }
      catch (error)
      {
        if (request.functions?.onSubmitTime)
        {
          request.functions.onSubmitTime(conversation);
        }
      }

      setDataConversation(prev =>
      {
        if (!prev.conversation) return prev;
        return {
          ...prev,
          conversation: {
            ...prev.conversation,
            TotalReplies: prev.conversation.TotalReplies + 1,
          },
        };
      });
    },
    [request.api, request.functions, handleUpdateCustomDetailsExternal],
  );

  // Restore hide replies highlight
  const restoreHideRepliesHighlight = useCallback((restoreIndicator = false) =>
  {
    setReplyToHighlight(null);
    if (restoreIndicator)
    {
      setShouldUpdateHideReplies(false);
    }
  }, []);
  void restoreHideRepliesHighlight;

  // Remove update button
  const removeUpdateButton = useCallback(() =>
  {
    if (updateCustomFields || updateConversation) return;

    if (updateReply)
    {
      // TODO: Check repliesPendingToHighlight
      setShowReloadButton(false);
      setUpdateReply(false);
    }
  }, [updateCustomFields, updateConversation, updateReply]);

  // Activate dark mode
  const activateDarkMode = useCallback(() =>
  {
    const darkMode = localStorage.getItem('C2_enableDarkMode');
    if (darkMode === 'true')
    {
      const html = document.documentElement;
      if (html.classList.contains('k-webkit'))
      {
        html.classList.add('dark-mode');
      }
    }
  }, []);

  // Sync connection ID from global singleton (non-infoWindow) and notify parent
  useEffect(() =>
  {
    if (request.isInfoWindow && request.params.SocketConnectionId) return;
    if (!globalSocketConnectionId) return;

    setSocketConnectionId(globalSocketConnectionId);
    setDataConversation(prev =>
    {
      if (!prev.conversation) return prev;
      return {
        ...prev,
        conversation: { ...prev.conversation, SocketConnectionId: globalSocketConnectionId },
      };
    });
    if (request.functions?.attachSocket)
    {
      request.functions.attachSocket(globalSocketConnectionId);
    }
  }, [globalSocketConnectionId]);

  // Subscribe to ShowRefreshButton events only when opened from an InfoWindow.
  // Dashboard card views and Home view render many instances simultaneously — subscribing
  // in every card would create N listeners per event and overload the component tree.
  // The SocketConnectionId is still passed down for write operations regardless.
  useEffect(() =>
  {
    if (!request.isInfoWindow) return;
    return socketService.on(
      'socket:client:ShowRefreshButton',
      (data: SocketShowRefreshButtonData) =>
      {
        handleSocketRefreshButton(data);
      },
    );
  }, [handleSocketRefreshButton, request.isInfoWindow]);

  // Effects
  useEffect(() =>
  {
    // For infoWindow with existing connection, notify parent once
    if (
      request.isInfoWindow &&
      request.params.SocketConnectionId &&
      request.functions?.attachSocket
    )
    {
      request.functions.attachSocket(request.params.SocketConnectionId);
    }

    // Attach react functions
    if (request.functions?.attachReactFunctions)
    {
      const paramFunctions: ReactFunctions = {
        onUpdateConversation: handleUpdateConversationExternal,
        onAddReply: handleAddReply,
        onUpdateReply: handleUpdateExternalReply,
        onDeleteConversation: handleDeleteConversation,
        onDeleteReply: handleDeleteReply,
        onAddLayerToCardBack: handleAddLayerToCardBack,
        onReloadConversation: handleReloadConversation,
        onReloadConversationInfo: handleReloadConversationInfo,
        onRestoreConversation: handleRestoreConversation,
        onUpdateSocketConnectionId: handleUpdateSocketConnectionId,
        onUpdateCustomDetails: handleUpdateCustomDetailsExternal,
        onUpdateTab: handleUpdateTab,
      };

      request.functions.attachReactFunctions(paramFunctions);

      if (request.functions.getReloadItemFn)
      {
        request.functions.getReloadItemFn(getConversation, request.params.ConversationId);
      }

      if ((request.functions as any).getUpdateItemFn)
      {
        (request.functions as any).getUpdateItemFn(
          handleUpdateConversationPhoto,
          request.params.ConversationId,
        );
      }
    }
  }, []);

  // Build DataConversation object from conversation data
  const buildDataConversation = useCallback(
    (conversationData: ConversationDetail, phaseId: number | null): DataConversation =>
    {
      // Fix image URLs
      if (conversationData.Replies)
      {
        for (let x = 0; x < conversationData.Replies.length; x++)
        {
          if (conversationData.Replies[x]!.ImageName === 'Images/user.png')
          {
            const baseUrl = request.config.weburl.replace(/\/$/, '');
            conversationData.Replies[x]!.ImageName = baseUrl + '/Images/user.png';
          }
        }
      }

      if (request.params.DashboardGroupId)
      {
        conversationData.DashboardGroupId = request.params.DashboardGroupId;
      }

      return {
        conversation: conversationData,
        realItemTypes: request.realItemTypes,
        api: request.api,
        corgeeFunctions: request.functions,
        isConversationDetailsView: request.isConversationDetailsView ?? true,
        onUpdateConversation: handleUpdateConversation,
        onAddReply: handleAddReply,
        onDeleteReply: handleDeleteReply,
        onUpdateReply: handleUpdateReply,
        onCreateNewItem: handleCreateNewItem,
        onDuplicateItem: handleDuplicateItem,
        onConvertTo: handleConvertTo,
        onAddLayerToCard: handleAddLayerToCard,
        onAddLayerToProject: handleAddLayerToProject,
        onSetFunctionsCardLayers: setFunctionsLayers,
        SocketConnectionId:
          socketConnectionId !== '' ? socketConnectionId : request.params.SocketConnectionId || '',
        configMenus: request.params?.ConfigMenus,
        isInfoWindow: request.isInfoWindow ?? false,
        getForceReloadDetailsFn: handleGetForceReloadDetailsFn,
        selectedPhaseId: phaseId,
      };
    },
    [
      request,
      socketConnectionId,
      handleUpdateConversation,
      handleAddReply,
      handleDeleteReply,
      handleUpdateReply,
      handleCreateNewItem,
      handleDuplicateItem,
      handleConvertTo,
      handleAddLayerToCard,
      handleAddLayerToProject,
      handleGetForceReloadDetailsFn,
    ],
  );

  useEffect(() =>
  {
    if (hasFetchedConversationRef.current === request.params.ConversationId) return;
    hasFetchedConversationRef.current = request.params.ConversationId;

    // If conversation data is provided via props, use it as initial placeholder for fast display,
    // then always fetch fresh data silently so complete fields (e.g. Attachments) are loaded.
    const hasInitialData = !!request.conversation;
    if (hasInitialData)
    {
      const dataConv = buildDataConversation(request.conversation!, null);
      setDataConversation(dataConv);
      sendDatatoCallback(request.conversation!);

      // Title view with layers already in the payload: skip the per-card fetch (avoids Home N+1)
      const providedLayers = (request.conversation as any).ConversationLayers;
      const hasLayers = Array.isArray(providedLayers) && providedLayers.length > 0;
      if (request.isViewCard === false && hasLayers)
      {
        setIsLoading(false);
        return;
      }
    }

    // Load via API — silent background refresh if initial data was already displayed
    if (request.params.PhaseId)
    {
      getConversation(
        request.params.ConversationId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        null,
        hasInitialData,
        request.params.PhaseId,
      );
    }
    else
    {
      getConversation(
        request.params.ConversationId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        null,
        hasInitialData,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.params.ConversationId]);

  // Re-fetch when the parent increments ForceReload (e.g. user clicks the refresh notification).
  // Uses a separate effect so it doesn't conflict with the initial-load gate above.
  useEffect(() =>
  {
    const forceReload = (request.params as any).ForceReload;
    if (!forceReload) return;
    getConversation(request.params.ConversationId, '', true, false, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(request.params as any).ForceReload]);

  // Create conversation handler
  const handleCreateConversation = useCallback((conv: any, itemType?: any) =>
  {
    editCardInitRef.current?.(conv, itemType);
    editCardRef.current?.openModal();
  }, []);

  // Open EditCard when the Edit action is triggered from the action menu
  const handleEditConversation = useCallback((_e: any, conv: any) =>
  {
    editCardInitRef.current?.(conv);
    editCardRef.current?.openModal();
  }, []);
  // Open DuplicateCard when the Edit action is triggered from the action menu
  const handleDuplicateConversation = useCallback((_e: any, conv: any) =>
  {
    duplicateCardInitRef.current?.(conv);
    duplicateCardRef.current?.openModal();
  }, []);

  const infoWindowContext = useContext(InfoWindowContext);

  const handleDeleteConversationFromView = React.useCallback(
    (_e: any, conv: any) =>
    {
      setDataConversation(prev =>
      {
        if (!prev.conversation) return prev;
        return { ...prev, conversation: { ...prev.conversation, IsDeleted2: true } };
      });
      setShowErrorMessage(true);
      request.functions?.deleteConversation?.(_e, conv);
    },
    [request.functions],
  );

  const enhancedCorgeeFunctions = React.useMemo(
    () => ({
      ...request.functions,
      editConversation: handleEditConversation,
      onDuplicateItem: handleDuplicateConversation,
      createConversation: handleCreateConversation,
      deleteConversation: handleDeleteConversationFromView,
      openInfoWindow: (conv: any) =>
      {
        // Linked cards pass the id as a DOM attribute string
        const id = normalizeConversationId(conv);
        if (!id) return;
        // Inside an InfoWindow: stack a new one via the context
        if (request.isInfoWindow && infoWindowContext)
        {
          infoWindowContext.openInfoWindow(id);
        }
        // Honor the caller-provided handler (e.g. Home columns 2/3)
        else if (request.functions?.openInfoWindow)
        {
          request.functions.openInfoWindow(id);
        }
        // Standalone ConversationDetails page: it has its own InfoWindowProvider
        else if (infoWindowContext)
        {
          infoWindowContext.openInfoWindow(id);
        }
        // Last resort: no InfoWindow available, open the conversation in a new tab
        else
        {
          window.open(buildConversationDetailsUrl(id), '_blank');
        }
      },
    }),
    [
      request.functions,
      request.isInfoWindow,
      handleEditConversation,
      handleDuplicateConversation,
      handleCreateConversation,
      infoWindowContext,
      handleDeleteConversationFromView,
    ],
  );

  // Sync title updates from ProjectHeader or other sources
  useConversationBus('title-updated', request.params.ConversationId, data =>
  {
    setDataConversation(prev =>
    {
      if (!prev.conversation) return prev;
      return { ...prev, conversation: { ...prev.conversation, Title: data.title } };
    });
  });

  // Sync red flag and important flag changes from InfoWindow or dashboard cards
  useConversationBus('redflag-updated', request.params.ConversationId, data =>
  {
    setDataConversation(prev =>
    {
      if (!prev.conversation) return prev;
      return { ...prev, conversation: { ...prev.conversation, IsRedFlag: data.isRedFlag } };
    });
  });

  useConversationBus('important-updated', request.params.ConversationId, data =>
  {
    setDataConversation(prev =>
    {
      if (!prev.conversation) return prev;
      return { ...prev, conversation: { ...prev.conversation, IsImportant: data.isImportant } };
    });
  });

  useConversationBus('completed-updated', request.params.ConversationId, data =>
  {
    setDataConversation(prev =>
    {
      if (!prev.conversation) return prev;
      return { ...prev, conversation: { ...prev.conversation, Finished: data.isCompleted, reactSelfUpdate: true } };
    });
  });

  // Sync reply additions from SubmitTime.
  // 1) Adds the reply immediately to state if not already present (optimistic update).
  // 2) Triggers a fresh getConversation reload to update custom fields (estimated/remaining/actual).
  // NOTE: useConversationBus uses callbackRef internally so getConversation here is always fresh.
  useConversationBus('reply-added', request.params.ConversationId, data =>
  {
    setDataConversation(prev =>
    {
      if (!prev.conversation || prev.conversation.ConversationId !== data.conversationId)
        return prev;
      // Skip if already added via the direct onaddreply callback
      const exists = (prev.conversation.Replies || []).some(
        r => r.ReplyId > 0 && r.ReplyId === data.reply.ReplyId,
      );
      if (exists) return prev;
      const copy = _.cloneDeep(prev);
      copy.conversation!.Replies = (copy.conversation!.Replies || []).filter(r => r.ReplyId > 0);
      copy.conversation!.Replies.push(data.reply);
      if (data.reply.OriginalConversationId > 0)
      {
        copy.conversation!.TotalTasks += 1;
      }
      else
      {
        copy.conversation!.TotalReplies += 1;
      }
      return copy;
    });
    // Reload from API to refresh custom fields (estimated/remaining/actual hours).
    // preserveReplies=true so the truncated Replies page this reload gets back
    // (getAllReplies=false) doesn't collapse an already-expanded "See more" list.
    getConversation(data.conversationId, '', false, false, 0, false, null, true, null, true);
  });

  // Sync reply updates from SubmitTime edits + reload custom fields
  useConversationBus('reply-updated', request.params.ConversationId, data =>
  {
    handleUpdateReply(data.conversationId, data.replyId, data.reply, undefined, false);
    getConversation(data.conversationId, '', false, false, 0, false, null, true, null, true);
  });

  // Reload conversation when it's updated (e.g., when a related card is created from this card)
  useConversationBus('conversation-updated', request.params.ConversationId, data =>
  {
    if (!data.updates?.refreshCustomFields && !data.updates?.relatedCardCreated) return;

    const newConv = data.updates?.relatedCardCreated ? data.newConversationData : null;
    const newConvId: number =
      newConv?.ConversationId ?? newConv?.Conversation?.ConversationId ?? 0;

    // preserveReplies keeps the rendered window, which would drop the task reply the backend
    // generated for the new card, so merge that one reply in from the response.
    const mergeTaskReply = newConvId > 0
      ? (response: ConversationDetail) =>
      {
        const taskReply = (response.Replies || []).find(
          r => r.OriginalConversationId === newConvId,
        );
        if (!taskReply) return;
        setDataConversation(prev =>
        {
          if (!prev.conversation || prev.conversation.ConversationId !== response.ConversationId)
            return prev;
          const replies = prev.conversation.Replies || [];
          if (replies.some(r => r.ReplyId === taskReply.ReplyId)) return prev;
          return {
            ...prev,
            conversation: { ...prev.conversation, Replies: [...replies, taskReply] },
          };
        });
      }
      : null;

    getConversation(data.conversationId, '', false, false, 0, false, mergeTaskReply, true, null, true);
  });

  // Timestamp of the last dashboard-refresh event for this conversation.
  // Using a timestamp (instead of a boolean) means any reloadKey change within
  // 15 seconds re-applies the highlight, even if the component remounted multiple
  // times due to cascading socket reloads.
  const lastDashboardRefreshRef = useRef<number>(0);

  useConversationBus('dashboard-refresh', request.params.ConversationId, () =>
  {
    lastDashboardRefreshRef.current = Date.now();
  });

  useConversationBus('dashboard-refresh-body', request.params.ConversationId, () =>
  {
    conversationItemRef.current?.highlightCard?.();
  });

  useEffect(() =>
  {
    if (reloadKey === 0) return;
    if (Date.now() - lastDashboardRefreshRef.current > 15000) return;
    conversationItemRef.current?.highlightCard?.();
  }, [reloadKey]);

  // Render
  activateDarkMode();

  const isRecycleBin = request.RecycleBin === true;
  const conversation = dataConversation.conversation;
  const isDeleted2 = (conversation as any)?.IsDeleted2;

  return (
    <div className="conversation-details-view-component">
      {request.enableLoading && isLoading && <ConversationItemAnimation />}

      {request.showErrorMessage && !isLoading && showErrorMessage && (
        <div className="corgee-unauthorized-item" style={{ minHeight: '70vh', position: 'relative' }}>
          {/* The "X" closes the hosting InfoWindow; on the standalone details page
              there is nothing to close, so it would be a dead control. */}
          {request.isInfoWindow === true && (
            <div
              className="icon-incorrect-icon"
              style={{
                position: 'absolute',
                top: '16px',
                right: '18px',
                cursor: 'pointer',
                fontSize: '16px',
              }}
              onClick={() => (request.functions as any)?.onCloseInfoWindow?.()}
            />
          )}
          <img
            style={{ width: '96px', display: 'block' }}
            src="https://services.corgee.com/services/GifFiles/2.gif"
            alt=""
          />
          <div style={{ fontSize: '2.5vh', textAlign: 'center' }}>
            You don't have access to this item.
          </div>
        </div>
      )}

      {!isLoading && !(request.showErrorMessage && showErrorMessage) &&
        (conversation && !isDeleted2 ? (
          <div>
            {showReloadButton && (
              <div className="react-update-workspace-container react-item-details-refresh-button">
                <input
                  type="button"
                  className="react-update-workspace-button"
                  value="This Item has changed, click here to refresh"
                  onClick={() => handleReloadConversationInfo()}
                />
              </div>
            )}
            <ol
              id={`detail_${conversation.ConversationId}`}
              className={
                isRecycleBin
                  ? 'sortableDashboard dashboardGroupSelector notifications-list group-column notColumn disabledDiv'
                  : 'sortableDashboard dashboardGroupSelector notifications-list group-column notColumn'
              }
            >
              {React.createElement(ConversationItem as any, {
                // Keyed by ConversationId (stable) instead of reloadKey so the silent
                // background refresh updates the card via props/componentDidUpdate instead
                // of remounting it — which previously unmounted/remounted CustomFields and
                // caused a visible flicker ~1s after opening. reloadKey still drives the
                // dashboard-refresh highlight effect below; switching conversations still
                // remounts because the ConversationId changes.
                key: conversation.ConversationId,
                ref: conversationItemRef,
                index: 1,
                api: dataConversation.api,
                corgeeFunctions: enhancedCorgeeFunctions,
                onChangedLayers: handleChangedLayers,
                onUpdateLayers: handleUpdateLayers,
                conversation: conversation,
                isViewCard: request.isViewCard ?? true,
                isInfowindowViewCard: request.isInfowindowViewCard,
                isInfowindowDrag: request.isInfowindowDrag,
                isInfoWindowChat: request.isInfoWindowChat,
                onUpdateConversation: handleUpdateConversation,
                onAddReply: handleAddReply,
                onUpdateReply: handleUpdateReply,
                onDeleteReply: handleDeleteReply,
                onCreateNewItem: handleCreateNewItem,
                onCreateConversation: handleCreateConversation,
                onDuplicateItem: handleDuplicateItem,
                onAddLayerToCard: handleAddLayerToCard,
                onAddLayerToProject: handleAddLayerToProject,
                hasArchivedFilter: request.hasArchivedFilter,
                realItemTypes: dataConversation.realItemTypes,
                configMenus: dataConversation.configMenus,
                config: request.config,
                storageData: request.storageData,
                chatData: request.chatData,
                userData: request.userData,
                isConversationDetailsView: request.isConversationDetailsView,
                replyToHighlight: replyToHighlight,
                shouldUpdateHideReplies: shouldUpdateHideReplies,
                SocketConnectionId: socketConnectionId,
                removeUpdateButton: removeUpdateButton,
                onConvertTo: handleConvertTo,
                onChangeFalseLayerCard: handleChangeFalseLayerCard,
                ShowMoveto: request.params.ShowMoveTo,
                UpdateCustomFields: request.functions?.UpdateCustomFields,
                UpdateCustomFieldsInternal: handleUpdateCustomDetailsExternal,
                onSubmitTime: handleSubmitTime,
                onStopUpdateTab: handleStopUpdateTab,
                UpdateTab: updateTab,
                onReloadConversation: handleReloadConversation,
                HasItemCreationPermission: request.HasItemCreationPermission,
                selectedPhaseId: dataConversation.selectedPhaseId,
                hideCustomFields: request.hideCustomFields,
                hideActionMenu: request.hideActionMenu,
                isHomeView: request.isHomeView,
                initialTabIndex: activeTabRef.current,
                onTabChange: (idx: number) => { activeTabRef.current = idx; },
                initialShowDetails: activeShowDetailsRef.current,
                onShowDetailsChange: (show: boolean) =>
                {
                  activeShowDetailsRef.current = show;
                  if (!show) activeTabRef.current = 0;
                },
              })}
            </ol>
          </div>
        ) : (
          <div></div>
        ))}

      <EditCard
        ref={editCardRef}
        config={{
          apiUrl: request.api.url,
          authToken: request.api.token,
          SocketConnectionId: globalSocketConnectionId || socketConnectionId || '',
          attachReactFunctions: (fns: any) =>
          {
            editCardInitRef.current = fns.initConfig;
          },
          functions: {
            loadingBarStart: (request.functions as any)?.loadingBarStart ?? (() => {}),
            loadingBarComplete: (request.functions as any)?.loadingBarComplete ?? (() => {}),
            addLayerToCard: (request.functions as any)?.addLayerToCard,
            conversationResponse: (_payload: any, data: any) =>
            {
              if (data?.ConversationId)
              {
                getConversation(data.ConversationId);
              }
            },
          },
          params: { Conversation: {} },
        }}
        UpdateCustomFields={() => {}}
      />
      <DuplicateCard
        ref={duplicateCardRef}
        config={{
          apiUrl: request.api.url,
          authToken: request.api.token,
          SocketConnectionId: socketConnectionId,
          attachReactFunctions: (fns: any) =>
          {
            duplicateCardInitRef.current = fns.initConfig;
          },
          DashboardId: 0,
          functions: {
            loadingBarStart: () => {},
            loadingBarComplete: () => {},
            conversationResponse: (_payload: any, data: any) =>
            {
              if (data?.ConversationId)
              {
                getConversation(data.ConversationId);
              }
            },
            addConversation: (conversation: any, dashboardGroupId: number) =>
            {
              if (dashboardGroupId)
              {
                conversationBus.emit('conversation-duplicated-from-infowindow', {
                  conversation,
                  dashboardGroupId,
                  socketConnectionId,
                });
              }
            },
          },
          params: { Conversation: {} },
        }}
        UpdateCustomFields={() => {}}
      />
    </div>
  );
};

export default ConversationDetailsView;
